import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { CampaignFunnelStage } from '@/shared/types/campaigns';

import { formatMetricValue } from './formatMetric';
import { FunnelRibbon } from './FunnelRibbon';
import { FUNNEL_RATE_LABELS, FUNNEL_STAGE_LABELS } from './metricConfig';

interface Props {
  stages: CampaignFunnelStage[];
  isLoading?: boolean;
}

/**
 * Stage colour ramp: one brand blue at rising strength, since the stages are one flow
 * and not separate categories. Anything stronger buries the rate pills.
 */
const RAMP_START = 8;
const RAMP_END = 53;

const stageColor = (index: number, total: number) => {
  const strength =
    RAMP_START + Math.round((index / Math.max(total - 1, 1)) * (RAMP_END - RAMP_START));
  return `color-mix(in srgb, var(--chart-1) ${strength}%, transparent)`;
};

/**
 * Outreach conversion: the recovery funnel, each stage's rate pinned to the boundary
 * it leads into.
 *
 * The ribbon is drawn strictly to scale, so a campaign with no promises tapers to
 * nothing — the counts underneath carry the numbers.
 */
export const OutreachConversion = ({ stages, isLoading }: Props) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-xl" />;
  }

  if (stages.length === 0) {
    return null;
  }

  const ribbonStages = stages.map((stage, index) => ({
    key: stage.key,
    value: stage.value,
    color: stageColor(index, stages.length),
  }));

  return (
    <section className="rounded-xl border border-stroke bg-background p-6">
      <h2 className="mb-6 text-base leading-6 font-semibold text-foreground">
        {t('campaigns.performance.sections.conversion')}
      </h2>

      {/* Rate pills overlay the segment boundaries: segment i morphs from stage i to
          stage i+1, so the entry to stage j sits at j/N across. */}
      <div className="relative h-56 w-full">
        <FunnelRibbon stages={ribbonStages} />

        {stages.map((stage, index) => {
          if (stage.rate == null) {
            return null;
          }
          return (
            <div
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              data-testid="funnel-rate-pill"
              key={stage.key}
              style={{ left: `${(index / stages.length) * 100}%` }}
            >
              <div className="rounded-md border border-stroke bg-background px-2 py-1 text-center shadow-sm">
                <p className="text-xs leading-4 font-medium whitespace-nowrap text-foreground-muted">
                  {t(FUNNEL_RATE_LABELS[stage.key] ?? stage.key)}
                </p>
                <p className="text-sm leading-5 font-semibold text-foreground">
                  {formatMetricValue(stage.rate, 'percent')}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Counts and stage names, one cell per segment so they line up with the ribbon. */}
      <div
        className="mt-2 grid"
        style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}
      >
        {stages.map((stage) => (
          <div className="px-1 text-center" key={stage.key}>
            <p className="text-base leading-6 font-semibold text-foreground">
              {formatMetricValue(stage.value, 'count')}
            </p>
            <p className="text-xs leading-4 text-foreground-muted">
              {t(FUNNEL_STAGE_LABELS[stage.key] ?? stage.key)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};
